import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Globe,
  Activity,
  ExternalLink
} from 'lucide-react';
import { getSites, createSite, updateSite, deleteSite, updateSiteStatus } from '../utils/siteApi';
import { mockSites, siteStatuses } from '../utils/mockSiteData';
import { uploadSiteLogo, getFileUrl, createDefaultSiteLogo, cleanupTempFiles } from '../utils/fileUpload';
import { S3_BASE_URL } from '../config/api';
import SubmitNotification from '../components/SubmitNotification';

const Sites = () => {
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [usingMockData, setUsingMockData] = useState(false);
  const [filters, setFilters] = useState({
    site_status: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [submitNotification, setSubmitNotification] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    site_name: '',
    site_slug: '',
    site_status: 'active',
    created_at: '',
    logo: null
  });

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      site_name: '',
      site_slug: '',
      site_status: 'active',
      created_at: '',
      logo: null
    });
    setFormErrors({});
  };

  // Fetch sites from API
  useEffect(() => {
    fetchSites();
    
    // Cleanup temp files on component unmount
    return () => {
      cleanupTempFiles();
    };
  }, []);

  // Filter sites when search term or filters change
  useEffect(() => {
    filterSites();
  }, [sites, searchTerm, filters]);

  const fetchSites = async () => {
    try {
      setLoading(true);
      const response = await getSites();
      
      console.log('🔍 Raw API Response:', response);
      
      if (response.success && response.data) {
        // Handle different API response structures
        let sitesData = response.data;
        
        // Log the data structure for debugging
        console.log('📊 Response data structure:', {
          hasData: !!response.data,
          dataType: typeof response.data,
          isArray: Array.isArray(response.data),
          keys: response.data ? Object.keys(response.data) : [],
          dataValue: response.data
        });
        
        // Try different common response structures
        if (response.data.list) {
          console.log('📋 Found "list" property');
          sitesData = response.data.list;
        } else if (response.data.records) {
          console.log('📋 Found "records" property');
          sitesData = response.data.records;
        } else if (response.data.sites) {
          console.log('📋 Found "sites" property');
          sitesData = response.data.sites;
        } else if (response.data.items) {
          console.log('📋 Found "items" property');
          sitesData = response.data.items;
        } else if (response.data.results) {
          console.log('📋 Found "results" property');
          sitesData = response.data.results;
        } else if (response.data.data) {
          console.log('📋 Found nested "data" property');
          sitesData = response.data.data;
        }
        
        console.log('🔍 Final sitesData:', sitesData);
        console.log('🔍 sitesData type:', typeof sitesData);
        console.log('🔍 sitesData isArray:', Array.isArray(sitesData));
        
        if (Array.isArray(sitesData)) {
          console.log('✅ Sites data is an array, setting sites state');
          setSites(sitesData);
          setUsingMockData(false);
        } else {
          console.warn('⚠️ Sites data is not an array, using mock data');
          console.warn('Expected array, got:', typeof sitesData, sitesData);
          
          // Try to convert to array if it's an object with numeric keys
          if (typeof sitesData === 'object' && sitesData !== null) {
            const keys = Object.keys(sitesData);
            const numericKeys = keys.filter(key => !isNaN(parseInt(key)));
            
            if (numericKeys.length > 0) {
              console.log('🔄 Converting object with numeric keys to array');
              const convertedArray = numericKeys.map(key => sitesData[key]);
              if (Array.isArray(convertedArray)) {
                console.log('✅ Successfully converted to array');
                setSites(convertedArray);
                setUsingMockData(false);
                return;
              }
            }
          }
          
          setSites(mockSites);
          setUsingMockData(true);
        }
      } else {
        console.warn('⚠️ API response not successful, using mock data');
        console.warn('Response:', response);
        setSites(mockSites);
        setUsingMockData(true);
      }
    } catch (error) {
      console.error('❌ Error fetching sites:', error);
      setSites(mockSites);
      setUsingMockData(true);
    } finally {
      setLoading(false);
    }
  };

  const filterSites = () => {
    let filtered = [...sites];

         // Search filter
     if (searchTerm) {
       filtered = filtered.filter(site => 
         site.name?.toLowerCase().includes(searchTerm.toLowerCase())
       );
     }

         // Status filter
     if (filters.site_status) {
       filtered = filtered.filter(site => site.site_status === filters.site_status);
     }

    // Sort by creation date descending (newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Descending order
    });

    setFilteredSites(filtered);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      site_status: ''
    });
    setSearchTerm('');
  };


  const handleAddSite = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditSite = (site) => {
    setCurrentSite(site);
    setFormData({
      site_name: site.site_name || '',
      site_slug: site.site_slug || '',
      site_status: site.site_status || 'active',
      created_at: site.created_at || '',
      logo: site.logo || null
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle logo file selection (store file, don't upload yet)
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Store the file in formData, upload will happen on form submit
      setFormData(prev => ({ ...prev, logo: file }));
      setFormErrors(prev => (prev.logo ? { ...prev, logo: '' } : prev));
      console.log('📁 Logo file selected:', file.name);
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
  };


  const handleDeleteSite = (site) => {
    setCurrentSite(site);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.site_name.trim()) {
      errors.site_name = 'Site Name is required';
    }
    if (!formData.site_slug.trim()) {
      errors.site_slug = 'Slug is required';
    }
    if (!formData.logo) {
      errors.logo = 'Please upload a Site Logo';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setFormSubmitting(true);
    setSubmitNotification({ type: 'loading', message: showAddModal ? 'Creating site...' : 'Updating site...' });

    try {
      if (usingMockData) {
        // Handle mock data operations
        if (showAddModal) {
          // Create new site in mock data
          console.log('Creating new site in mock data:', formData);
          const today = new Date().toISOString().split('T')[0]; // Format as YYYY-MM-DD
          const newSite = {
            id: Date.now(), // Generate a temporary ID
            ...formData,
            created_at: formData.created_at || today, // Use provided date or today
            updated_at: new Date().toISOString()
          };
          setSites(prev => [...prev, newSite]);
          setShowAddModal(false);
          resetForm();
          setSubmitNotification({ type: 'success', message: 'Site created successfully.' });
        } else {
          // Update existing site in mock data
          console.log('Updating site in mock data:', currentSite.id, formData);
          setSites(prev => prev.map(site => 
            site.id === currentSite.id 
              ? { ...site, ...formData, updated_at: new Date().toISOString() }
              : site
          ));
          setShowEditModal(false);
          setCurrentSite(null);
          setSubmitNotification({ type: 'success', message: 'Site updated successfully.' });
        }
      } else {
        // Handle real API operations
        // Prepare form data for submission
        const submitData = { ...formData };
        
        // Automatically set created_at to today's date when creating a new site
        if (showAddModal && !submitData.created_at) {
          const today = new Date();
          submitData.created_at = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        }
        
        // Upload logo file if it's a File object
        if (formData.logo instanceof File) {
          console.log('📤 Uploading logo file:', formData.logo.name);
          try {
            const logoPath = await uploadSiteLogo(formData.logo);
            console.log('✅ Logo uploaded successfully, received path:', logoPath);
            
            if (logoPath && logoPath !== null && logoPath !== 'null') {
              submitData.logo = logoPath;
              console.log('🔗 Logo URL will be included in site data:', submitData.logo);
            } else {
              console.error('❌ Logo path is null or invalid:', logoPath);
              alert('Error: Logo upload returned invalid path');
              return;
            }
          } catch (uploadError) {
            console.error('❌ Logo upload failed:', uploadError);
            alert(`Logo upload failed: ${uploadError.message}`);
            return;
          }
        } else if (formData.logo) {
          console.log('📋 Using existing logo URL:', formData.logo);
        }
        
        if (showAddModal) {
          // Create new site via API
          console.log('🏗️ Creating new site with data:', submitData);
          console.log('📡 Sending to /api/sites with logo URL:', submitData.logo);
          console.log('🔍 Final submitData structure:', JSON.stringify(submitData, null, 2));
          const response = await createSite(submitData);
          
          if (response.success) {
            console.log('✅ Site created successfully:', response.data);
            await fetchSites();
            setShowAddModal(false);
            resetForm();
            setSubmitNotification({ type: 'success', message: 'Site created successfully.' });
          } else {
            console.error('❌ Failed to create site:', response.message);
            setSubmitNotification({ type: 'error', message: `Failed to create site: ${response.message || 'Unknown error'}` });
          }
        } else {
          // Update existing site via API
          console.log('🔄 Updating site:', currentSite.site_id, submitData);
          console.log('📡 Sending to /api/sites/{id} with logo URL:', submitData.logo);
          console.log('🔍 Final submitData structure:', JSON.stringify(submitData, null, 2));
          const response = await updateSite(currentSite.site_id, submitData);
          
          if (response.success) {
            console.log('✅ Site updated successfully:', response.data);
            await fetchSites();
            setShowEditModal(false);
            setCurrentSite(null);
            setSubmitNotification({ type: 'success', message: 'Site updated successfully.' });
          } else {
            console.error('❌ Failed to update site:', response.message);
            setSubmitNotification({ type: 'error', message: `Failed to update site: ${response.message || 'Unknown error'}` });
          }
        }
      }
    } catch (error) {
      console.error('❌ Error in form submission:', error);
      setSubmitNotification({ type: 'error', message: error.message || 'Something went wrong.' });
    } finally {
      setFormSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (currentSite) {
      try {
        if (usingMockData) {
          // Handle mock data deletion
          console.log('Deleting site in mock data:', currentSite.id);
          setSites(prev => prev.filter(site => site.id !== currentSite.id));
          setShowDeleteModal(false);
          setCurrentSite(null);
          alert('✅ Site deleted successfully (using mock data)');
        } else {
          // Handle real API deletion
          await deleteSite(currentSite.id);
          setSites(prev => prev.filter(site => site.id !== currentSite.id));
          setShowDeleteModal(false);
          setCurrentSite(null);
        }
      } catch (error) {
        console.error('Error deleting site:', error);
        alert(`Error deleting site: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: '●' },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: '●' }
    };

    const config = statusConfig[status] || statusConfig.inactive;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        {status}
      </span>
    );
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SubmitNotification notification={submitNotification} onDismiss={() => setSubmitNotification(null)} />
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
          <p className="text-gray-600 ">Manage your website properties and monitor their performance.</p>
          {usingMockData && (
            <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full mr-1.5"></span>
              Using Mock Data
            </div>
          )}
        </div>
        <div className="flex space-x-3 py-4">
          <button
            onClick={handleAddSite}
            className="btn-primary flex items-center space-x-2"
            style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
          >
            <Plus className="h-5 w-5" />
            <span>Add Site</span>
          </button>
        </div>
      </div>

             {/* Stats Cards */}
       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
         <div className="card">
           <div className="flex items-center">
             <div className="p-3 rounded-lg bg-blue-500">
               <Globe className="h-6 w-6 text-white" />
             </div>
             <div className="ml-4">
               <p className="text-sm font-medium text-gray-600">Total Sites</p>
               <p className="text-2xl font-bold text-gray-900">{sites.length}</p>
             </div>
           </div>
         </div>
         
         <div className="card">
           <div className="flex items-center">
             <div className="p-3 rounded-lg bg-green-500">
               <Activity className="h-6 w-6 text-white" />
             </div>
             <div className="ml-4">
               <p className="text-sm font-medium text-gray-600">Active Sites</p>
               <p className="text-2xl font-bold text-gray-900">
                 {sites.filter(site => site.site_status === 'active').length}
               </p>
             </div>
           </div>
         </div>
       </div>


      {/* Sites Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Sites ({filteredSites.length})
          </h3>
        </div>

        <div className="overflow-x-auto table-container">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Actions
                 </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Site
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Slug
                 </th>
                 
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Logo
                 </th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Created At
                 </th>
                 
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSites.length === 0 ? (
                <tr>
                                     <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    {searchTerm || Object.values(filters).some(f => f) 
                      ? 'No sites match your search criteria' 
                      : 'No sites found'
                    }
                  </td>
                </tr>
              ) : (
                filteredSites.map((site) => (
                  <tr key={site.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                                                 <button
                           onClick={() => window.open('/' + site.site_slug, '_blank')}
                           className="text-blue-600 hover:text-blue-900"
                           title="Visit Site"
                           style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                         >
                           <ExternalLink className="h-4 w-4" />
                         </button>
                        <button
                          onClick={() => handleEditSite(site)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit Site"
                          style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {/*<button
                          onClick={() => handleDeleteSite(site)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Site"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>*/}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                            <Globe className="h-6 w-6 text-primary-600" />
                          </div>
                        </div>
                                                 <div className="ml-4">
                           <div className="text-sm font-medium text-gray-900">{site.site_name || site.site_name}</div>
                           {/*<div className="text-sm text-gray-500">{site.site_url}</div>*/}
                         </div>
                      </div>
                    </td>
                                         <td className="px-6 py-4 whitespace-nowrap">
                       {getStatusBadge(site.site_status)}
                     </td>
                                         <td className="px-6 py-4 whitespace-nowrap">
                       <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                         {site.slug || site.site_slug || 'N/A'}
                       </span>
                     </td>
                     
                     <td className="px-6 py-4 whitespace-nowrap">
                       {site.logo ? (
                         <div className="w-10 h-10 border border-gray-300 rounded-md overflow-hidden bg-gray-50">
                           <img 
                             src={`${S3_BASE_URL}/${site.logo}`} 
                             alt={`${site.name || site.site_name} logo`} 
                             className="w-full h-full object-cover"
                             onError={(e) => {
                               e.target.style.display = 'none';
                               e.target.nextSibling.style.display = 'flex';
                             }}
                           />
                           <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs" style={{display: 'none'}}>
                             No Logo
                           </div>
                         </div>
                       ) : (
                         <div className="w-10 h-10 border border-gray-300 rounded-md bg-gray-100 flex items-center justify-center">
                           <span className="text-gray-400 text-xs">No Logo</span>
                         </div>
                       )}
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                       {site.created_at || 'N/A'}
                     </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Site Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {showAddModal ? 'Add New Site' : 'Edit Site'}
              </h3>

              {Object.keys(formErrors).length > 0 && (
                <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
                  <p className="text-sm font-medium text-red-800 mb-1">Please fix the following:</p>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-0.5">
                    {formErrors.site_name && <li>{formErrors.site_name}</li>}
                    {formErrors.site_slug && <li>{formErrors.site_slug}</li>}
                    {formErrors.logo && <li>{formErrors.logo}</li>}
                  </ul>
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Name *</label>
                  <input
                    type="text"
                    value={formData.site_name}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, site_name: e.target.value }));
                      if (formErrors.site_name) setFormErrors(prev => ({ ...prev, site_name: '' }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${formErrors.site_name ? 'border-red-500' : 'border-gray-300'}`}
                    required
                  />
                  {formErrors.site_name && <p className="mt-1 text-sm text-red-600">{formErrors.site_name}</p>}
                </div>
                
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                   <input
                     type="text"
                     value={formData.site_slug}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, site_slug: e.target.value }));
                      if (formErrors.site_slug) setFormErrors(prev => ({ ...prev, site_slug: '' }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${showEditModal ? 'bg-gray-100 cursor-not-allowed' : ''} ${formErrors.site_slug ? 'border-red-500' : 'border-gray-300'}`}
                    required
                    disabled={showEditModal}
                   />
                   {formErrors.site_slug && <p className="mt-1 text-sm text-red-600">{formErrors.site_slug}</p>}
                 </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="w-full">
                     <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                     <select
                       value={formData.site_status}
                       onChange={(e) => setFormData(prev => ({ ...prev, site_status: e.target.value }))}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                     >
                       {siteStatuses.map(status => (
                         <option key={status} value={status}>{status}</option>
                       ))}
                     </select>
                   </div>
                   
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Site Logo <span className="text-red-600">*</span></label>
                     <div className="space-y-3">
                       {/* Logo Preview - only when editing (not when adding new site) */}
                       {!showAddModal && formData.logo && (
                         <div className="flex items-center space-x-5">
                           <div className="w-16 h-16 border border-gray-300 rounded-md overflow-hidden bg-gray-50">
                             <img 
                               src={formData.logo instanceof File ? URL.createObjectURL(formData.logo) : `${S3_BASE_URL}/${formData.logo}`}
                               alt="Site logo" 
                               className="w-full h-full object-cover"
                               onError={(e) => {
                                 e.target.style.display = 'none';
                                 e.target.nextSibling.style.display = 'flex';
                               }}
                             />
                             <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs" style={{display: 'none'}}>
                               Invalid Image
                             </div>
                           </div>
                           <div className="flex flex-col space-y-1">
                             <button
                               type="button"
                               onClick={handleRemoveLogo}
                               className="text-red-600 hover:text-red-800 text-sm"
                               style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                             >
                               Remove
                             </button>
                             {formData.logo instanceof File && (
                               <span className="text-xs text-gray-500">
                                 {formData.logo.name}
                               </span>
                             )}
                           </div>
                         </div>
                       )}
                       
                       {/* File Upload */}
                       <div className="space-y-2">
                         <input
                           type="file"
                           accept="image/*"
                           onChange={handleLogoUpload}
                           className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                         />
                         <p className="text-xs text-gray-500">
                           PNG, JPG, GIF up to 5MB
                         </p>
                         {formErrors.logo && <p className="text-sm text-red-600">{formErrors.logo}</p>}
                       </div>
                   </div>
                   </div>
                 </div>
                
                
                
                
                
                                 <div className="flex justify-end space-x-3 pt-4">
                   <button
                     type="button"
                     onClick={() => setShowAddModal(false) || setShowEditModal(false)}
                     className="btn-secondary"
                     disabled={formSubmitting}
                     style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                   >
                     Cancel
                   </button>
                   <button
                     type="submit"
                     className="btn-primary"
                     disabled={formSubmitting}
                     style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                   >
                     {formSubmitting ? (
                       <div className="flex items-center space-x-2">
                         <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                         <span>{showAddModal ? 'Creating...' : 'Updating...'}</span>
                       </div>
                     ) : (
                       showAddModal ? 'Add Site' : 'Update Site'
                     )}
                   </button>
                 </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Site</h3>
                                 <p className="text-sm text-gray-500 mt-2">
                     Are you sure you want to delete "{currentSite?.name || currentSite?.site_name}"? This action cannot be undone.
                   </p>
              <div className="flex justify-center space-x-3 mt-6">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn-secondary"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="btn-danger"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sites;
